/**
 * Minimal typed fetch client used by the customer site and the admin console.
 * - same-origin cookies (HttpOnly session), never stores tokens in localStorage
 * - attaches X-CSRF-Token to every non-GET request
 * - attaches Idempotency-Key when the caller supplies one (money-moving requests)
 */
import type { ApiError } from "../shared/api.ts";

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;
  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

let csrfToken: string | null = null;
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}
export function getCsrfToken(): string | null {
  return csrfToken;
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export interface RequestOptions {
  idempotencyKey?: string;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

async function send<T>(method: string, path: string, body: BodyInit | undefined, contentType: string | null, opts: RequestOptions): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json", ...(opts.headers ?? {}) };
  if (contentType) headers["Content-Type"] = contentType;
  if (method !== "GET" && csrfToken) headers["X-CSRF-Token"] = csrfToken;
  if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;
  const res = await fetch(path, { method, headers, body, credentials: "same-origin", signal: opts.signal });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const err = (data as ApiError | null)?.error;
    if (err?.code === 'shop_session_closed' && !path.startsWith('/api/auth/login')) window.dispatchEvent(new CustomEvent('wb24:shop-session-closed', { detail: err.message }));
    throw new ApiRequestError(res.status, err?.code ?? `http_${res.status}`, err?.message ?? res.statusText, err?.details);
  }
  return data as T;
}

function request<T>(method: string, path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  return send<T>(method, path, body === undefined ? undefined : JSON.stringify(body), body === undefined ? null : "application/json", opts);
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>("GET", path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("POST", path, body ?? {}, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("PUT", path, body ?? {}, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("PATCH", path, body ?? {}, opts),
  del: <T>(path: string, opts?: RequestOptions) => request<T>("DELETE", path, undefined, opts),
  /** multipart/form-data POST (file uploads); the browser sets the boundary. */
  postForm: <T>(path: string, form: FormData, opts: RequestOptions = {}) => send<T>("POST", path, form, null, opts),
};

/** Opens a same-origin WebSocket (ws/wss chosen from the page protocol). */
export function openSocket(path: string): WebSocket {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return new WebSocket(`${proto}//${location.host}${path}`);
}
